
import React from "react";
import { Box, Typography, Button, IconButton } from "@mui/material";
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from "react-router-dom";

const COLORS = {
    bg: '#f5f5fa',
    white: '#fff',
    bd: '#dddde6',
    teal: '#0d9488',
    tealBg: 'rgba(13,148,136,.07)',
    tealGlow: 'rgba(13,148,136,.22)',
    purple: '#7c3aed',
    purpleBg: 'rgba(124,58,237,.07)',
    purpleGlow: 'rgba(124,58,237,.18)',
    amberBg: 'rgba(217,119,6,.07)',
    tx: '#18162a',
    tx2: '#5e5c72',
};

const DecodeStress = () => {
    const navigate = useNavigate();

    return (
        <Box sx={{
            minHeight: {
                xs: 'calc(100vh - 56px)',
                sm: 'calc(100vh - 64px)',
                md: 'calc(100vh - 70px)',
            },
            py: { xs: 3, sm: 4 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            px: 2,
            position: 'relative',
        }}>
            {/* Top bar — stays exactly where it is, not part of the centering group */}
            <Box
                sx={{
                    width: "100%",
                    maxWidth: 1200,
                    position: "relative",
                    display: "flex",
                    alignItems: "flex-start",
                    mb: 2,
                    flexShrink: 0,
                }}
            >
                {/* Branding */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: "13px",
                            background: "linear-gradient(135deg,#0d9488,#7c3aed)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 20,
                        }}
                    >
                        🌟
                    </Box>

                    <Box>
                        <Typography
                            sx={{
                                fontFamily: '"Fraunces", Georgia, serif',
                                fontSize: 20,
                                fontWeight: 700,
                                lineHeight: 1.1,
                                background: "linear-gradient(135deg,#0d9488,#7c3aed)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                            }}
                        >
                            echoShine
                        </Typography>

                        <Typography sx={{ fontSize: 11, color: "#5e5c72" }}>
                            Student Wellness Assessment
                        </Typography>
                    </Box>
                </Box>

                {/* Back Button */}
                <IconButton
                    onClick={() => navigate("/echo-sounds")}
                    sx={{
                        position: "absolute",
                        right: 0,
                        top: 0,
                        backgroundColor: "#F0F5FF",
                        width: 36,
                        height: 36,
                        "&:hover": { backgroundColor: "#E0EBFF" },
                    }}
                >
                    <ArrowBackIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* Everything below the branding centers itself in the remaining space */}
            <Box sx={{
                flex: 1,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
            }}>
                {/* Card */}
                <Box sx={{
                    width: { xs: '100%', sm: '90%', md: 420 },
                    maxWidth: 420,
                    background: COLORS.white,
                    border: `1px solid ${COLORS.bd}`,
                    borderRadius: '22px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 12px rgba(0,0,0,.04)',
                }}>
                    <Box sx={{
                        padding: { xs: '36px 20px 28px', sm: '44px 24px 36px' },
                        textAlign: 'center',
                        background: `linear-gradient(160deg, ${COLORS.tealBg.replace('.07', '.05')}, ${COLORS.purpleBg.replace('.07', '.05')})`,
                        position: 'relative',
                        overflow: 'hidden',
                    }}>
                        <Typography sx={{ fontSize: { xs: 28, sm: 36 }, letterSpacing: '10px', mb: 2.25 }}>
                            🧘 🔋 🎵
                        </Typography>

                        <Typography sx={{
                            fontFamily: '"Fraunces", Georgia, serif',
                            fontSize: { xs: 21, sm: 26 },
                            fontWeight: 800,
                            color: COLORS.tx,
                            mb: 1.25,
                            lineHeight: 1.2,
                        }}>
                            Decode Your Stress.<br />Let Echo Sounds Reset You.
                        </Typography>

                        <Typography sx={{
                            fontSize: { xs: 13, sm: 14 },
                            color: COLORS.tx2,
                            lineHeight: 1.65,
                            maxWidth: 310,
                            mx: 'auto',
                        }}>
                            Tap where your body holds tension, check your energy levels, and discover the therapeutic sound frequencies that can help you feel balanced again.
                        </Typography>
                    </Box>

                    <Box sx={{ padding: '22px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        <Feature bg={COLORS.tealBg} icon="👆" title="Body Scan" text="tap where you feel tension on an interactive body map" />
                        <Feature bg={COLORS.purpleBg} icon="🔋" title="Energy Battery" text="set your mental energy level and identify what's draining you" />
                        <Feature bg={COLORS.amberBg} icon="🎵" title="Sound Therapy Rx" text="get frequency-matched tracks based on your combined profile" />
                    </Box>
                </Box>

                {/* Begin Assessment button */}
                <Button
                    onClick={() => navigate("/assessment-flow")}
                    sx={{
                        mt: '14px',
                        width: { xs: '100%', sm: '90%', md: 420 },
                        maxWidth: 420,
                        py: '15px',
                        borderRadius: '14px',
                        fontFamily: '"DM Sans", sans-serif',
                        fontWeight: 600,
                        fontSize: 14,
                        color: '#fff',
                        textTransform: 'none',
                        background: `linear-gradient(135deg, ${COLORS.teal}, ${COLORS.purple})`,
                        boxShadow: `0 4px 16px ${COLORS.purpleGlow}`,
                        transition: 'all 0.25s',
                        '&:hover': {
                            background: `linear-gradient(135deg, ${COLORS.teal}, ${COLORS.purple})`,
                            transform: 'translateY(-2px)',
                            boxShadow: `0 8px 28px ${COLORS.purpleGlow}`,
                        },
                    }}
                >
                    Begin Assessment
                </Button>
            </Box>
        </Box>
    );
};

const Feature = ({ bg, icon, title, text }) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
        <Box sx={{
            width: 36, height: 36,
            borderRadius: '11px',
            background: bg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 17,
            flexShrink: 0,
        }}>
            {icon}
        </Box>
        <Typography sx={{ fontSize: 13, color: '#5e5c72', lineHeight: 1.5 }}>
            <Box component="strong" sx={{ color: '#18162a', fontWeight: 600 }}>{title}</Box> — {text}
        </Typography>
    </Box>
);

export default DecodeStress;